import {
  LinkOpenMode,
  LinkTargetKind,
  ResolvedTabIdentity,
  RouteEntity,
  StoredTabIdentity,
  TabHandoff,
  TabSequence,
} from './tab-tracking.types'

/** `sessionStorage` key holding the tab identity (app-agnostic on purpose) */
export const TAB_ID_STORAGE_KEY = 'wm-tab-id'

/** `sessionStorage` key written on `pagehide`, read back by the next document of the same tab */
export const TAB_UNLOAD_MARKER_KEY = 'wm-tab-unloaded-at'

/** `localStorage` key of the per-session tab counter shared by every tab */
export const TAB_SEQ_STORAGE_KEY = 'wm-tab-seq'

/** `localStorage` key of the handoffs left by a tab for the tabs it opens */
export const TAB_HANDOFF_STORAGE_KEY = 'wm-tab-handoffs'

/** A handoff older than this is ignored: the new tab did not come from that click */
const TAB_HANDOFF_MAX_AGE_MS = 15_000

/** `BroadcastChannel` used for the live tab count. Channels are per origin, so one name serves every app */
export const TABS_CHANNEL_NAME = 'wm-tabs'

/** A tab sequence is restarted when the last tab was opened longer ago than this */
const TAB_SEQUENCE_MAX_AGE_MS = 4 * 60 * 60 * 1000

/** Query params that must never reach an analytics tool */
const SENSITIVE_QUERY_PARAMS = ['token', 'access_token', 'id_token']

const SENSITIVE_QUERY_PARAM_PATTERN = new RegExp(
  `([?&])(?:${SENSITIVE_QUERY_PARAMS.join('|')})=[^&#]*(&?)`,
  'i'
)

/** Numeric ids, Mongo ids and UUIDs */
const ID_SEGMENT_PATTERN =
  /^(\d+|[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/** Path segments that sit between an entity name and its id (`repair-requests/details/:id`) */
const NON_ENTITY_SEGMENTS = ['details']

/** Query params that describe the list view rather than its filters */
const NON_FILTER_QUERY_PARAMS = [
  'page',
  'pagination',
  'order',
  'activeSort',
  'selected',
  'source',
  ...SENSITIVE_QUERY_PARAMS,
]

/**
 * List routes reported by `list_state` (matched against the normalized route).
 * Admin lists: tasks, repair requests (ongoing, csm, supply, ...), units (shutdown, by-status),
 * invoices, overtime, audit, operations monitoring.
 */
const LIST_ROUTE_PATTERNS: RegExp[] = [
  /^\/platform\/tasks\/?$/,
  /^\/platform\/repair-requests(\/(ongoing|done|denied|canceled|csm|supply|technical-support|repair-team))?\/?$/,
  /^\/platform\/units(\/(shutdown|by-status|to-onboard))?\/?$/,
  /^\/platform\/invoices(\/[a-z-]+)?\/?$/,
  /^\/platform\/overtime(\/[a-z-]+)?\/?$/,
  /^\/platform\/audit(\/[a-z-]+)*\/?$/,
  /^\/platform\/operations-monitoring\/(tasks|missions)\/?$/,
]

/**
 * Generates a random tab id, falling back to a non-crypto id where `randomUUID` is unavailable
 * @param {Crypto | null | undefined} cryptoRef
 * @returns {string}
 */
export function generateTabId(
  cryptoRef: Crypto | null | undefined = globalThis.crypto
): string {
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Parses the identity stored in `sessionStorage`, returning null on anything unexpected
 * @param {string | null} raw
 * @returns {StoredTabIdentity | null}
 */
export function parseStoredTabIdentity(
  raw: string | null
): StoredTabIdentity | null {
  try {
    const parsed = raw ? JSON.parse(raw) : null
    return typeof parsed?.id === 'string' &&
      typeof parsed?.openedAt === 'number' &&
      typeof parsed?.seq === 'number'
      ? parsed
      : null
  } catch {
    return null
  }
}

/**
 * Decides whether the current document keeps the stored tab identity or starts a new tab.
 *
 * `sessionStorage` is per tab and survives reloads, but "duplicate tab" (and `window.open`
 * with an opener) copies it into the new tab. Every document of this app writes an unload
 * marker on `pagehide`, so a document that finds an identity *without* that marker, outside
 * of a reload, and older than itself, is running in a copy: it gets a new identity.
 */
export function resolveTabIdentity(input: {
  stored: StoredTabIdentity | null
  hasUnloadMarker: boolean
  navigationType: string | undefined
  documentStartedAt: number
  now: number
  generateId: () => string
  nextSeq: () => number
}): ResolvedTabIdentity {
  const { stored } = input
  const copiedSession =
    !!stored &&
    !input.hasUnloadMarker &&
    input.navigationType !== 'reload' &&
    stored.openedAt < input.documentStartedAt

  if (stored && !copiedSession) {
    return { ...stored, isNew: false, copiedSession: false }
  }
  return {
    id: input.generateId(),
    openedAt: input.now,
    seq: input.nextSeq(),
    isNew: true,
    copiedSession,
  }
}

/**
 * Computes the next value of the tab counter shared by every tab of a session.
 * The counter restarts when the RUM session changes or after a long pause.
 */
export function computeNextTabSequence(
  previous: TabSequence | null,
  sessionId: string | null,
  now: number
): TabSequence {
  const sameSession =
    !!previous &&
    now - previous.at < TAB_SEQUENCE_MAX_AGE_MS &&
    (!sessionId || !previous.sessionId || previous.sessionId === sessionId)
  return {
    sessionId,
    seq: sameSession && previous ? previous.seq + 1 : 1,
    at: now,
  }
}

/**
 * Parses the stored tab counter
 * @param {string | null} raw
 * @returns {TabSequence | null}
 */
export function parseTabSequence(raw: string | null): TabSequence | null {
  try {
    const parsed = raw ? JSON.parse(raw) : null
    return typeof parsed?.seq === 'number' && typeof parsed?.at === 'number'
      ? parsed
      : null
  } catch {
    return null
  }
}

/**
 * Removes auth tokens (`?token=` and friends) from a URL, a path or a search string
 * @param {string} url
 * @returns {string}
 */
export function scrubSensitiveParams<T extends string | null | undefined>(
  url: T
): T {
  if (!url) {
    return url
  }
  let scrubbed: string = url
  let previous: string
  do {
    previous = scrubbed
    scrubbed = scrubbed.replace(
      SENSITIVE_QUERY_PARAM_PATTERN,
      (_match, separator: string, trailingAmpersand: string) =>
        trailingAmpersand ? separator : ''
    )
  } while (scrubbed !== previous)
  return scrubbed.replace(/[?&](?=#|$)/, '') as T
}

/**
 * Returns the path without query string, fragment or matrix params
 * @param {string} url
 * @returns {string}
 */
export function getPathFromUrl(url: string): string {
  return (url || '').split(/[?#;]/)[0] || '/'
}

/**
 * Replaces ids in a path by `:id` so that routes can be aggregated
 * @param {string} url
 * @returns {string}
 */
export function normalizeRoute(url: string): string {
  return getPathFromUrl(url)
    .split('/')
    .map((segment) => (ID_SEGMENT_PATTERN.test(segment) ? ':id' : segment))
    .join('/')
}

/**
 * Guesses the entity a path is about, from its last id segment and the name before it.
 * `/platform/repair-requests/details/123` gives `repair-requests` / `123`,
 * `/platform/address/device-details/abc/overview` gives `device` / `abc`.
 */
export function extractRouteEntity(url: string): RouteEntity {
  const segments = getPathFromUrl(url).split('/').filter(Boolean)
  const idIndex = segments
    .map((segment) => ID_SEGMENT_PATTERN.test(segment))
    .lastIndexOf(true)
  if (idIndex < 0) {
    return { entityType: null, entityId: null }
  }
  const entityType = segments
    .slice(0, idIndex)
    .reverse()
    .find(
      (segment) =>
        !NON_ENTITY_SEGMENTS.includes(segment) &&
        !ID_SEGMENT_PATTERN.test(segment)
    )
  return {
    entityType: entityType ? entityType.replace(/-details$/, '') : null,
    entityId: segments[idIndex],
  }
}

/**
 * Splits a URL into host and path, returning nulls when it cannot be parsed
 * @param {string} url
 * @returns {{ host: string | null; path: string | null }}
 */
export function parseUrlParts(url: string | null | undefined): {
  host: string | null
  path: string | null
} {
  try {
    const parsed = new URL(url as string)
    return { host: parsed.host, path: parsed.pathname }
  } catch {
    return { host: null, path: null }
  }
}

/**
 * Reads a query param from a search string
 * @param {string} search
 * @param {string} key
 * @returns {string | null}
 */
export function getQueryParam(search: string, key: string): string | null {
  try {
    return new URLSearchParams(search).get(key)
  } catch {
    return null
  }
}

/**
 * Tells how a mouse event on a link opens it, or undefined when it stays in the same tab.
 * Middle-click fires `auxclick` (not `click`); ctrl / cmd / shift + click open a new tab or window.
 * @param {Pick<MouseEvent, 'type' | 'button' | 'ctrlKey' | 'metaKey' | 'shiftKey'>} event
 * @param {string | null} anchorTarget the `target` attribute of the link
 * @returns {LinkOpenMode | undefined}
 */
export function detectLinkOpenMode(
  event: Pick<
    MouseEvent,
    'type' | 'button' | 'ctrlKey' | 'metaKey' | 'shiftKey'
  >,
  anchorTarget: string | null
): LinkOpenMode | undefined {
  if (event.type === 'auxclick') {
    return event.button === 1 ? LinkOpenMode.MiddleClick : undefined
  }
  if (event.type !== 'click' || event.button !== 0) {
    return undefined
  }
  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    return LinkOpenMode.CtrlClick
  }
  return anchorTarget === '_blank' ? LinkOpenMode.TargetBlank : undefined
}

/**
 * Describes where a link points to, without its query string
 * @param {string} href an absolute URL or a path
 * @param {string} currentOrigin the origin of the app
 */
export function describeLinkTarget(
  href: string,
  currentOrigin: string
): {
  targetHost: string | null
  targetKind: LinkTargetKind
  targetRoute: string | null
  targetPath: string | null
} {
  let url: URL
  try {
    url = new URL(href, currentOrigin)
  } catch {
    return {
      targetHost: null,
      targetKind: LinkTargetKind.Other,
      targetRoute: null,
      targetPath: null,
    }
  }
  return {
    targetHost: url.host,
    targetKind: getLinkTargetKind(url, currentOrigin),
    targetRoute: normalizeRoute(url.pathname),
    targetPath: url.pathname,
  }
}

function getLinkTargetKind(url: URL, currentOrigin: string): LinkTargetKind {
  if (url.origin === currentOrigin) {
    return LinkTargetKind.Internal
  }
  if (/(^|\.)(force|salesforce)\.com$/.test(url.hostname)) {
    return LinkTargetKind.Salesforce
  }
  if (/(^|\.)netsuite\.com$/.test(url.hostname)) {
    return LinkTargetKind.Netsuite
  }
  if (/(^|\.)wemaintain\.(com|io)$/.test(url.hostname)) {
    return LinkTargetKind.Wemaintain
  }
  return LinkTargetKind.Other
}

/**
 * Reads the query params of a URL or path as a plain object (last value wins)
 * @param {string} url
 * @returns {Record<string, string>}
 */
export function parseQueryParams(url: string): Record<string, string> {
  const queryIndex = (url || '').indexOf('?')
  if (queryIndex < 0) {
    return {}
  }
  const search = url.slice(queryIndex + 1).split('#')[0]
  const params: Record<string, string> = {}
  try {
    new URLSearchParams(search).forEach((value, key) => (params[key] = value))
  } catch {
    return {}
  }
  return params
}

/**
 * Keeps the filter query params of a list (non empty, not pagination / order / selection)
 * @param {Record<string, string>} queryParams
 * @returns {Record<string, string>} sorted by key
 */
export function getFilterParams(
  queryParams: Record<string, string>
): Record<string, string> {
  return Object.keys(queryParams)
    .filter(
      (key) =>
        !NON_FILTER_QUERY_PARAMS.includes(key) &&
        queryParams[key] !== '' &&
        queryParams[key] !== undefined &&
        queryParams[key] !== null
    )
    .sort()
    .reduce<Record<string, string>>((filters, key) => {
      filters[key] = queryParams[key]
      return filters
    }, {})
}

/**
 * Stable short hash (FNV-1a, 32 bits) of filter params, independent of key order
 * @param {Record<string, string>} filters
 * @returns {string} 8 hex characters
 */
export function hashFilters(filters: Record<string, string>): string {
  const serialized = JSON.stringify(
    Object.keys(filters)
      .sort()
      .map((key) => [key, filters[key]])
  )
  let hash = 0x811c9dc5
  for (let index = 0; index < serialized.length; index++) {
    hash ^= serialized.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * Whether a normalized route is one of the lists reported by `list_state`
 * @param {string} route
 * @returns {boolean}
 */
export function isListRoute(route: string): boolean {
  return LIST_ROUTE_PATTERNS.some((pattern) => pattern.test(route))
}

/**
 * Parses the handoffs stored in `localStorage`
 * @param {string | null} raw
 * @returns {TabHandoff[]}
 */
export function parseTabHandoffs(raw: string | null): TabHandoff[] {
  try {
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter(
          (handoff) =>
            typeof handoff?.path === 'string' && typeof handoff?.at === 'number'
        )
      : []
  } catch {
    return []
  }
}

/**
 * Drops the handoffs that are too old to belong to a tab being opened now
 * @param {TabHandoff[]} handoffs
 * @param {number} now
 * @returns {TabHandoff[]}
 */
export function pruneTabHandoffs(
  handoffs: TabHandoff[],
  now: number
): TabHandoff[] {
  return handoffs.filter(
    (handoff) => handoff.at <= now && now - handoff.at < TAB_HANDOFF_MAX_AGE_MS
  )
}

/**
 * Finds the handoff left for a tab that just opened on `path`, and returns the fresh
 * handoffs left for the other tabs.
 */
export function takeTabHandoff(
  handoffs: TabHandoff[],
  path: string,
  now: number
): { handoff: TabHandoff | null; remaining: TabHandoff[] } {
  const fresh = pruneTabHandoffs(handoffs, now)
  const index = fresh.findIndex((handoff) => handoff.path === path)
  return {
    handoff: index >= 0 ? fresh[index] : null,
    remaining: fresh.filter((_handoff, position) => position !== index),
  }
}

/**
 * Returns the tag name of the closest `wm-front-*` component around an element
 * @param {Element | null} element
 * @returns {string | null}
 */
export function findSourceComponent(element: Element | null): string | null {
  let current: Element | null = element
  while (current) {
    const tagName = current.tagName?.toLowerCase()
    if (tagName?.startsWith('wm-front-')) {
      return tagName
    }
    current = current.parentElement
  }
  return null
}
